import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, finalize, map, takeUntil, tap } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import {
  GeoblockingTimeSlice,
  GeoblockingTimeSliceCreationParams,
  GeoblockingTimeSliceUpdatingParams
} from '~core/models/core.models';
import dayjs from 'dayjs';
import { DefaultModalHeaderComponent } from '~backoffice/app/~shared/components/modals/default-modal-header/default-modal-header.component';
import { CustomModalCardComponent } from '~backoffice/app/~shared/components/modals/custom-modal-card/custom-modal-card.component';
import { SharedModule } from '~backoffice/app/~shared/modules/shared.module';
import { GeoblockingService } from '~backoffice/app/~shared/services/geoblocking.service';
import { LiveStreamService } from '~core/services/live-stream.service';
import { LiveStreamListParams } from '~core/models/live-stream.models';
import { AccessControlsService } from '~core/services/access-controls.service';

@Component({
  selector: 'geoblocking-form',
  imports: [
    DefaultModalHeaderComponent,
    CustomModalCardComponent,
    SharedModule
  ],
  templateUrl: './geoblocking-form.component.html',
  styleUrls: ['./geoblocking-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeoblockingFormComponent implements OnInit, OnChanges, OnDestroy {
  geoblockingService = inject(GeoblockingService);
  liveStreamService = inject(LiveStreamService);
  accessControlsService = inject(AccessControlsService);
  globalFacade = inject(GlobalFacade);
  cdRef = inject(ChangeDetectorRef);
  dialogRef = inject(MatDialogRef<GeoblockingFormComponent>);
  snackbarService = inject(SnackbarService);

  @Input() geoblocking: GeoblockingTimeSlice | undefined;

  liveStreams$ = this.liveStreamService
    .list({ itemPerPage: 1000 } as LiveStreamListParams)
    .pipe(map(res => res.items));

  accessControl$ = this.accessControlsService
    .list()
    .pipe(map(res => res.items));

  violations$ = this.globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation => {
            const control = this.geoblockingForm.get(violation.propertyPath);
            control
              ? control.setErrors({
                  httpError: violation.title
                })
              : this.geoblockingForm.setErrors({ httpError: violation.title });
          });
          this.cdRef.detectChanges();
        }, 500);
      }
    })
  );

  isLoading = false;

  geoblockingForm = new FormGroup({
    liveStreamId: new FormControl<string | null>(null, {
      validators: [Validators.required]
    }),
    accessControlId: new FormControl<number | null>(null, {
      validators: [Validators.required]
    }),
    dateStartAt: new FormControl<Date | null>(null, {
      validators: [Validators.required]
    }),
    timeStartAt: new FormControl<string | null>(null, {
      validators: [Validators.required]
    }),
    dateEndAt: new FormControl<Date | null>(null, {
      validators: [Validators.required]
    }),
    timeEndAt: new FormControl<string | null>(null, {
      validators: [Validators.required]
    })
  });

  private _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this._handleEndDateChanges();
    this.patchFormValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('geoblocking' in changes) {
      this.patchFormValues();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.unsubscribe();
  }

  patchFormValues(): void {
    if (!this.geoblocking) return;
    const formattedTimeStartAt = dayjs(
      this.geoblocking.validFor.startDateTime
    ).format('HH:mm');
    const formattedTimeEndAt = dayjs(
      this.geoblocking.validFor.endDateTime
    ).format('HH:mm');
    this.geoblockingForm.patchValue({
      liveStreamId: this.geoblocking.liveStream.id,
      accessControlId: this.geoblocking.accessControlId,
      dateEndAt: dayjs(this.geoblocking.validFor.endDateTime).toDate(),
      dateStartAt: dayjs(this.geoblocking.validFor.startDateTime).toDate(),
      timeEndAt: formattedTimeEndAt,
      timeStartAt: formattedTimeStartAt
    });
    this.cdRef.detectChanges();
  }

  clearDate(date: 'startAt' | 'endAt'): void {
    if (date === 'endAt') {
      const dateEnd = this.geoblockingForm.get('dateEndAt');
      const timeEnd = this.geoblockingForm.get('timeEndAt');
      dateEnd?.setValue(null);
      timeEnd?.setValue(null);
      return;
    }
    const dateStart = this.geoblockingForm.get('dateStartAt');
    const timeStart = this.geoblockingForm.get('timeStartAt');
    dateStart?.setValue(null);
    timeStart?.setValue(null);
  }

  private _handleEndDateChanges(): void {
    this.geoblockingForm.controls.dateEndAt.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: value => {
            const startDateControl = this.geoblockingForm.controls.dateStartAt;
            if (value) {
              startDateControl.addValidators([Validators.required]);
              startDateControl.updateValueAndValidity();
            } else {
              startDateControl.removeValidators(Validators.required);
              startDateControl.updateValueAndValidity();
            }
          }
        })
      )
      .subscribe();
  }

  saveGeoblocking(): void {
    this.isLoading = true;
    if (!this.geoblockingForm.valid) return;

    const {
      liveStreamId,
      accessControlId,
      dateStartAt,
      dateEndAt,
      timeStartAt,
      timeEndAt
    } = this.geoblockingForm.value;

    const formattedDateStartAt = dayjs(dateStartAt).format('YYYY-MM-DD');
    const formattedDateEndAt = dayjs(dateEndAt).format('YYYY-MM-DD');

    const combinedStartAt = dayjs(
      `${formattedDateStartAt}T${timeStartAt}`
    ).format('YYYY-MM-DDTHH:mm:ssZ');
    const combinedEndAt = dayjs(`${formattedDateEndAt}T${timeEndAt}`).format(
      'YYYY-MM-DDTHH:mm:ssZ'
    );

    const data = {
      liveStreamId,
      accessControlId,
      validFor: {
        startDateTime: combinedStartAt,
        endDateTime: combinedEndAt
      }
    };

    const requests$ = this.geoblocking
      ? this.geoblockingService
          .update(
            this.geoblocking.id,
            data as unknown as GeoblockingTimeSliceUpdatingParams
          )
          .pipe(
            tap({
              next: () => {
                this.snackbarService.open({
                  text: `Configuration mise à jour.`,
                  type: 'success'
                });
              }
            })
          )
      : this.geoblockingService
          .create(data as unknown as GeoblockingTimeSliceCreationParams)
          .pipe(
            tap({
              next: () => {
                this.snackbarService.open({
                  text: `Configuration ajoutée.`,
                  type: 'success'
                });
              }
            })
          );

    requests$
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        }),
        tap({
          next: geoblocking => {
            this.dialogRef.close(geoblocking);
          }
        })
      )
      .subscribe();
  }
}
